<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui";
import type { DetachOutcome } from "../model/useServiceAssign";

/**
 * Confirm dialog for detaching a master from a service (improvement-199, ADR-0015).
 * States: idle → removing → blocked (409).
 * Cancel receives initial focus (ADR-0015). Action function injected as prop — no
 * direct composable reach-in from inside the dialog.
 */
const props = defineProps<{
  open: boolean;
  masterName: string;
  serviceName: string;
  detach: () => Promise<DetachOutcome>;
}>();

const emit = defineEmits<{
  "update:open": [value: boolean];
  detached: [];
}>();

const { t } = useI18n();

type DetachStatus = "idle" | "removing" | "blocked";
const detachStatus = ref<DetachStatus>("idle");

async function onConfirm(): Promise<void> {
  if (detachStatus.value === "removing") return;
  detachStatus.value = "removing";

  const outcome = await props.detach();

  if (outcome === "success") {
    detachStatus.value = "idle";
    emit("detached");
    emit("update:open", false);
  } else if (outcome === "blocked") {
    detachStatus.value = "blocked";
  } else {
    // network / server error: toast already shown by composable — return to idle
    detachStatus.value = "idle";
  }
}

function onCancel(): void {
  detachStatus.value = "idle";
  emit("update:open", false);
}
</script>

<template>
  <AlertDialog
    :open="open"
    @update:open="(v) => { if (!v) onCancel(); }"
  >
    <AlertDialogContent data-testid="detach-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>
          {{ t("services.detach.confirmTitle") }}
        </AlertDialogTitle>
        <AlertDialogDescription>
          {{ t("services.detach.confirmBody", { master: masterName, service: serviceName }) }}
          <!-- Blocked message: reactive after 409, role=alert for screen-reader announcement -->
          <span
            v-if="detachStatus === 'blocked'"
            role="alert"
            data-testid="detach-blocked-message"
          >
            {{ t("services.errors.serviceInUse") }}
          </span>
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter>
        <!-- ADR-0015: Cancel focused by default (destructive safety) -->
        <AlertDialogCancel
          data-testid="detach-cancel"
          @click="onCancel"
        >
          {{ detachStatus === "blocked" ? t("services.detach.close") : t("services.actions.cancel") }}
        </AlertDialogCancel>
        <AlertDialogAction
          variant="destructive"
          :disabled="detachStatus === 'blocked' || detachStatus === 'removing'"
          :aria-disabled="detachStatus === 'blocked' || detachStatus === 'removing'"
          data-testid="detach-confirm"
          @click.prevent="onConfirm"
        >
          {{ detachStatus === "removing" ? t("services.saving") : t("services.detach.confirm") }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
